// Scoring matrices based on Risk Factor 1: Buyer's Assessment
export interface ScoringMatrix {
  [key: string]: number;
}

// Buyer's Country Risk (Coface CRA) to Score mapping
export const COUNTRY_RISK_SCORES: ScoringMatrix = {
  A1: 10,
  A2: 9,
  A3: 8,
  A4: 7,
  B: 5,
  C: 4,
  D: 2,
  E: 0
};

// Industry/Product Risk to Score mapping
export const INDUSTRY_PRODUCT_RISK_SCORES: ScoringMatrix = {
  Pharmaceutical: 10,
  Telecom: 9,
  FMCG: 8,
  Chemicals: 7,
  'Paper/ Printing/ Packaging/ Wood': 6,
  'Agri-food /HoReCa/ IT / Electronics/ Household Equipment': 5,
  'Automotive / Energy/ Metals': 4,
  'Machinery & Equipment': 3,
  'Retail/ Textile/ Clothing/ Transport Equipment & Transportation': 2,
  'Bulk commodities/ Real Estate/ Construction': 1,
  'Weapons/ Precious Metals': 0
};

// Buyer's Ownership/Establishment to Score mapping
export const OWNERSHIP_ESTABLISHMENT_SCORES: ScoringMatrix = {
  'Public-listed co.': 10,
  'Subsidiary of a Public-listed co.': 9,
  'Public limited company': 8,
  'Limited Liability company': 7,
  'Private limited company': 6,
  Partnership: 5,
  'Family-owned business': 4,
  'Sole Propretorship': 3,
  'Gov. entity / SOE': 2,
  'On-going Restructuring': 1,
  'Closed / Insolvent / Dormant': 0
};

// Buyer's Activity to Score mapping
export const BUYER_ACTIVITY_SCORES: ScoringMatrix = {
  'MNC with various operations': 10,
  'Manufacturer - Large (>USD500M revenue)': 9,
  'Manufacturer - Medium (USD50-USD500M revenue)': 8,
  'Manufacturer - Small (up to USD 50M revenue)': 7,
  'Value-add trader/ service provider': 6,
  'Trader with warehouse facility': 5,
  'Trader via e-commerce platform': 4,
  'Trader without warehouse facility': 3,
  'Agent / Buying House/ Service Provider': 2,
  'Government-linked operations': 1,
  'Non-operating': 0
};

// Buyer's Vintage to Score mapping
export const BUYER_VINTAGE_SCORES: ScoringMatrix = {
  '> 10 years': 10,
  '9-10 years': 9,
  '8-9 years': 8,
  '7-8 years': 7,
  '6-7 years': 6,
  '4-6 years': 5,
  '2-4 years': 4,
  '1-2 years': 3,
  '6 months - 1 year': 2,
  '3-6 months': 1,
  '< 3 months': 0
};

// Buyer's Size (employees) to Score mapping
export const BUYER_SIZE_SCORES: ScoringMatrix = {
  '>1000 employees': 10,
  '500-1000 employees': 9,
  '300-500 employees': 8,
  '200-300 employees': 7,
  '100-200 employees': 6,
  '50-100 employees': 5,
  '30-50 employees': 4,
  '10-30 employees': 3,
  '5-10 employees': 2,
  '1-5 employees': 1,
  'No employees': 0
};

// Buyer's Turnover to Score mapping
export const BUYER_TURNOVER_SCORES: ScoringMatrix = {
  '> USD 2B': 10,
  'USD 1 - 2B': 9,
  'USD 500M - 1B': 8,
  'USD 200-500M': 7,
  'USD 100-200M': 6,
  'USD 50-100M': 5,
  'USD 25-50M': 4,
  'USD 10-25M': 3,
  'USD 5-10M': 2,
  'USD 1-5M': 1,
  '< USD 1M': 0
};

// Combined scoring function for Buyer's Assessment
export const calculateBuyerAssessmentScore = (
  countryRisk: string,
  industryProduct: string,
  ownership: string,
  activity: string,
  vintage: string,
  size: string,
  turnover: string
): number => {
  const countryScore = COUNTRY_RISK_SCORES[countryRisk] || 0;
  const industryScore = INDUSTRY_PRODUCT_RISK_SCORES[industryProduct] || 0;
  const ownershipScore = OWNERSHIP_ESTABLISHMENT_SCORES[ownership] || 0;
  const activityScore = BUYER_ACTIVITY_SCORES[activity] || 0;
  const vintageScore = BUYER_VINTAGE_SCORES[vintage] || 0;
  const sizeScore = BUYER_SIZE_SCORES[size] || 0;
  const turnoverScore = BUYER_TURNOVER_SCORES[turnover] || 0;

  // Calculate weighted average (all factors equally weighted for now)
  const totalScore = (countryScore + industryScore + ownershipScore + activityScore + vintageScore + sizeScore + turnoverScore) / 7;
  
  return Math.round(totalScore * 100) / 100; // Round to 2 decimal places
};

// Get score for individual field
export const getFieldScore = (fieldName: string, value: string): number => {
  switch (fieldName.toLowerCase()) {
    case 'country risk':
      return COUNTRY_RISK_SCORES[value] || 0;
    case 'industry':
    case 'industry/product risk':
      return INDUSTRY_PRODUCT_RISK_SCORES[value] || 0;
    case 'ownership':
    case 'ownership/establishment':
      return OWNERSHIP_ESTABLISHMENT_SCORES[value] || 0;
    case 'activity':
      return BUYER_ACTIVITY_SCORES[value] || 0;
    case 'vintage':
      return BUYER_VINTAGE_SCORES[value] || 0;
    case 'size':
    case 'size (employees)':
      return BUYER_SIZE_SCORES[value] || 0;
    case 'turnover':
      return BUYER_TURNOVER_SCORES[value] || 0;
    default:
      return 0;
  }
};

// TODO: Add Supplier's Assessment and Trading Relationship scoring matrices
// These would follow similar patterns but with different criteria

// Supplier's Assessment matrices (placeholder - to be implemented based on requirements)
export const SUPPLIER_OWNERSHIP_ESTABLISHMENT_SCORES: ScoringMatrix = {
  // Similar structure to buyer ownership but may have different scoring
  ...OWNERSHIP_ESTABLISHMENT_SCORES
};

export const SUPPLIER_ACTIVITY_SCORES: ScoringMatrix = {
  // Similar structure to buyer activity but may have different scoring
  ...BUYER_ACTIVITY_SCORES
};

export const SUPPLIER_VINTAGE_SCORES: ScoringMatrix = {
  // Similar structure to buyer vintage but may have different scoring
  ...BUYER_VINTAGE_SCORES
};

export const SUPPLIER_SIZE_SCORES: ScoringMatrix = {
  // Similar structure to buyer size but may have different scoring
  ...BUYER_SIZE_SCORES
};

export const SUPPLIER_TURNOVER_SCORES: ScoringMatrix = {
  // Similar structure to buyer turnover but may have different scoring
  ...BUYER_TURNOVER_SCORES
};

// Trading Relationship matrices (placeholder - to be implemented based on requirements)
export const TRADING_RELATIONSHIP_VINTAGE_SCORES: ScoringMatrix = {
  '> 5 years': 10,
  '4-5 years': 9,
  '3-4 years': 8,
  '2-3 years': 7,
  '1-2 years': 6,
  '6 months - 1 year': 5,
  '3-6 months': 4,
  '1-3 months': 3,
  '< 1 month': 2,
  'New relationship': 1,
  'No relationship': 0
};

export const TRADING_RELATIONSHIP_VOLUME_SCORES: ScoringMatrix = {
  '> USD 10M': 10,
  'USD 5-10M': 9,
  'USD 2-5M': 8,
  'USD 1-2M': 7,
  'USD 500K-1M': 6,
  'USD 200K-500K': 5,
  'USD 100K-200K': 4,
  'USD 50K-100K': 3,
  'USD 10K-50K': 2,
  '< USD 10K': 1,
  'No trading volume': 0
};

export const TRADING_RELATIONSHIP_PAYMENT_HISTORY_SCORES: ScoringMatrix = {
  'Always on time': 10,
  'Mostly on time (95%+)': 9,
  'Usually on time (90-95%)': 8,
  'Often on time (80-90%)': 7,
  'Sometimes on time (70-80%)': 6,
  'Occasionally on time (60-70%)': 5,
  'Rarely on time (50-60%)': 4,
  'Seldom on time (40-50%)': 3,
  'Usually late (20-40%)': 2,
  'Always late (<20%)': 1,
  'No payment history': 0
};

// Get score for supplier field
export const getSupplierFieldScore = (fieldName: string, value: string): number => {
  switch (fieldName.toLowerCase()) {
    case 'country risk':
      return COUNTRY_RISK_SCORES[value] || 0;
    case 'industry':
    case 'industry/product risk':
      return INDUSTRY_PRODUCT_RISK_SCORES[value] || 0;
    case 'ownership':
    case 'ownership/establishment':
      return SUPPLIER_OWNERSHIP_ESTABLISHMENT_SCORES[value] || 0;
    case 'activity':
      return SUPPLIER_ACTIVITY_SCORES[value] || 0;
    case 'vintage':
      return SUPPLIER_VINTAGE_SCORES[value] || 0;
    case 'size':
    case 'size (employees)':
      return SUPPLIER_SIZE_SCORES[value] || 0;
    case 'turnover':
      return SUPPLIER_TURNOVER_SCORES[value] || 0;
    default:
      return 0;
  }
};

// Get score for trading relationship field
export const getTradingRelationshipFieldScore = (fieldName: string, value: string): number => {
  switch (fieldName.toLowerCase()) {
    case 'vintage':
    case 'relationship vintage':
      return TRADING_RELATIONSHIP_VINTAGE_SCORES[value] || 0;
    case 'volume':
    case 'trading volume':
      return TRADING_RELATIONSHIP_VOLUME_SCORES[value] || 0;
    case 'payment history':
      return TRADING_RELATIONSHIP_PAYMENT_HISTORY_SCORES[value] || 0;
    default:
      return 0;
  }
};
